import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Error } from 'src/app/models/error.model';
import {
  GraphService,
  ResourceStatus,
} from 'src/app/services/graph/graph.service';
import { lastValueFrom } from 'rxjs';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { ResourceListFields } from 'src/app/models/resource-list-fields.model';
import { GECorrespondingTable } from 'src/app/models/ge-corresponding-table.model';

@Component({
  selector: 'app-resource-add-dialog',
  templateUrl: './resource-add-dialog.html',
  styleUrls: ['./resource-add-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ResourceAddDialogComponent implements OnInit {
  errorCreateRessource: Error = { error: { message: '', code: '' } };
  isErrorCreateRessource = false;
  isRessourceCreated = false;
  isBusyCreateRessource = false;

  error: Error = { error: { message: '', code: '' } };

  nameValidator = '^[a-z]+[a-z._-]*$';
  displayNameValidator = '^[a-zA-ZÀ-ÖØ-öø-ÿ\\s\\-]+$';
  capacityValidator = '^\\d+$';

  maxlength = 64;

  addRessourceForm: FormGroup;

  // Form to Bolean
  ajout_Directeur = false;
  ajout_ConseilDir = false;
  ajout_secDir = false;
  ajout_admin = false;
  ajout_Tech = false;
  ajout_ITRes = false;
  ajout_ITGest = false;
  ajout_PersonEns = false;

  access_Directeur = false;
  access_ConseilDir = false;
  access_secDir = false;
  access_admin = false;
  access_Tech = false;
  access_ITRes = false;
  access_ITGest = false;

  currentGE = '';
  currentGECorrespondingTable: GECorrespondingTable;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  selectedType: string;

  constructor(
    public matDialogRef: MatDialogRef<ResourceAddDialogComponent>,
    private _formBuilder: FormBuilder,
    private _graphService: GraphService,
    private _localStorageService: LocalStorageService
  ) {
    this.addRessourceForm = this._formBuilder.group({
      ressourceName: [
        '',
        [
          Validators.required,
          Validators.pattern(this.displayNameValidator),
          Validators.maxLength(this.maxlength),
        ],
      ],
      ressourceType: ['', Validators.required],
      capacity: ['', Validators.pattern(this.capacityValidator)],
      address: [''],
      locality: [''],
    });
  }

  ngOnInit(): void {
    this.currentGE = this._localStorageService.getItem<string>('userDefaultGE');
    this.currentGECorrespondingTable = {
      title: this.currentGE,
    };
    this.getGECorespondingTable();
  }

  async getGECorespondingTable(): Promise<void> {
    try {
      await lastValueFrom(
        this._graphService.selectGeInCorrespondingTable(
          this.currentGECorrespondingTable
        )
      ).then(ge => {
        this.currentGECorrespondingTable = ge;
      });
    } catch (error) {
      let customError: Error = { error: undefined };

      if (error.statusText == 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customError = error.error;
        }
        if (error.error.errors) {
          customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
        } else {
          if (error.error) {
            customError = error.error;
          }
        }
      }

      if (customError.error === undefined) {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      }
    }
  }

  createDisplayName() {
    let name = this.addRessourceForm.get('ressourceName').value;
    const type = this.addRessourceForm.get('ressourceType').value;

    if (name !== '' && type !== '') {
      if (type === 's') {
        name =
          'Salle ' + name + ' - ' + this.currentGECorrespondingTable.service;
      } else if (type === 'z') {
        name =
          'Zimmer ' + name + ' - ' + this.currentGECorrespondingTable.service;
      } else if (type === 'e') {
        name =
          'Ressource ' +
          name +
          ' - ' +
          this.currentGECorrespondingTable.service;
      }
    }

    return name;
  }

  createMailAddress() {
    let name = this.createMailAddressName();
    const type = this.addRessourceForm.get('ressourceType').value;
    const geAdresse = this.currentGECorrespondingTable.service.replace(
      ' ',
      '-'
    );

    if (name !== '' && type !== '') {
      name = type + '_' + name + '_' + geAdresse + '@edu.vs.ch';
    }

    return name;
  }

  createMailAddressName() {
    let name = this.addRessourceForm.get('ressourceName').value;
    if (name !== '') {
      name = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      name = name.replace(/[^a-zA-Z0-9._-]/g, '');
    }

    return name;
  }

  private fetchRessourceformData(): ResourceListFields {
    const rue = (this.addRessourceForm.get('address').value +
      ', ' +
      this.addRessourceForm.get('locality').value) as string;
    return {
      // Standard Config
      type: 'Ressource',
      etat_Ressource: ResourceStatus.CREATE,
      ge: this.firstCharToUpper(this.currentGE),
      typedelaressource: this.selectedType,

      // Forms
      title: this.createDisplayName(),

      adressedemessagerie: this.createMailAddressName(),

      capacit_x00e9_: this.addRessourceForm.get('capacity').value as string,
      rue: rue === ', ' ? null : rue,

      // Personnel technique
      rqarbipGrTechnique: this.firstCharToUpper(this.ajout_Tech),
      rqarFullAccessGrTechnique: this.firstCharToUpper(this.access_Tech),

      // Personnel administratif
      rqarbipGrAdministratif: this.firstCharToUpper(this.ajout_admin),
      rqarFullAccessGrAdministratif: this.firstCharToUpper(this.access_admin),

      // Secrétaire de direction
      rqarbipGrSecretaireDirection: this.firstCharToUpper(this.ajout_secDir),
      rqarFullAccessGrSecretaireDirect: this.firstCharToUpper(
        this.access_secDir
      ),

      // IT-Responsable
      rqarbipGrITResponsable: this.firstCharToUpper(this.ajout_ITRes),
      rqarFullAccessGrITResponsable: this.firstCharToUpper(this.access_ITRes),

      // IT-Gestionnaire
      rqarbipGrITGestionnaire: this.firstCharToUpper(this.ajout_ITGest),
      rqarFullAccessGrITGestionnaire: this.firstCharToUpper(this.access_ITGest),

      // Directeur
      rqarbipGrDirecteur: this.firstCharToUpper(this.ajout_Directeur),
      rqarFullAccessGrDirecteur: this.firstCharToUpper(this.access_Directeur),

      // Conseil de Direction
      rqarbipGrConseilDirection: this.firstCharToUpper(this.ajout_ConseilDir),
      rqarFullAccessGrConseilDirection: this.firstCharToUpper(
        this.access_ConseilDir
      ),

      // Personnel enseignant
      rqarbipGrPersonnelEnseignant: this.firstCharToUpper(this.ajout_PersonEns),
    };
  }

  async createRessource(): Promise<void> {
    this.errorCreateRessource = { error: { message: '', code: '' } };
    this.isErrorCreateRessource = false;
    this.isRessourceCreated = false;
    this.isBusyCreateRessource = true;

    try {
      const newRessource = this.fetchRessourceformData();

      await lastValueFrom(this._graphService.addRessource(newRessource)).then(
        () => {
          this.isRessourceCreated = true;
          this.isBusyCreateRessource = false;
        }
      );
    } catch (error) {
      let customError: Error = { error: undefined };

      if (error.statusText == 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customError = error.error;
        }
        if (error.error.errors) {
          customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
        } else {
          if (error.error) {
            customError = error.error;
          }
        }
      }

      if (customError.error === undefined) {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      }

      this.isErrorCreateRessource = true;
      this.errorCreateRessource = customError;
      this.isRessourceCreated = false;
      this.isBusyCreateRessource = false;
    }
  }

  private firstCharToUpper(input: string | boolean): string {
    return String(input).charAt(0).toUpperCase() + String(input).slice(1);
  }
}
